import create from 'zustand';
import produce from 'immer';
import { persist } from 'zustand/middleware';
import { FlowCollection } from '@groove/api/gateway/v1/flowCollections';
import useFlowStore from './useFlowStore';

export type HeaderMessage = {
  message: string;
  notificationAction?: () => void;
  notificationActionText?: string;
  type?: 'success' | 'error' | 'warning';
  timestamp?: number;
  additionalMessage?: string;
  keepOpen?: boolean;
};

export type SortBy = 'name' | 'created_at' | 'updated_at' | 'starred';
export type SortDirection = 'ASC' | 'DESC';

export type Filters = {
  is_starred: boolean | null;
  status: string | null;
  labels: number[];
  owner_ids: number[];
  shared_with: { id: number; type: string } | null;
  created_at: { start_date: string | null; end_date: string | null } | null;
  updated_at: { start_date: string | null; end_date: string | null } | null;
};
export type FilterField = keyof Filters;

export const initialFilters: Filters = {
  is_starred: null,
  status: null,
  labels: [],
  owner_ids: [],
  shared_with: null,
  created_at: null,
  updated_at: null,
};

export type Store = {
  activeView: 'flows' | 'collections' | 'collection_show';
  searchTerm: string;
  sortBy: SortBy;
  sortDirection: SortDirection;
  selectedFilters: Filters;
  activeCollection: FlowCollection | null;
  headerMessages: HeaderMessage[];
  refetchCollections: () => void;
  setActiveView: (view: string) => void;
  setSearchTerm: (query: string) => void;
  setSortBy: (sortBy: SortBy) => void;
  setSortDirection: (sortDirection: SortDirection) => void;
  setHeaderMessage: (message: HeaderMessage) => void;
  removeHeaderMessage: (timestamp: number) => void;
  setErrorMessage: (message: HeaderMessage) => void;
  setActiveCollection: (collection: FlowCollection) => void;
  setRefetchCollections: (refetch: () => void) => void;
  updateSelectedFilters: (newFilters: Filters) => void;
};

const useStore = create<Store>(
  persist(
    (set, get) => ({
      activeView: 'flows',
      searchTerm: '',
      sortBy: 'name',
      sortDirection: 'ASC',
      activeCollection: null,
      refetchCollections: null,
      selectedFilters: initialFilters,
      headerMessages: [],
      setActiveView: (view: string) =>
        set(
          produce(state => {
            state.activeView = view;
          })
        ),
      setSortBy: (sortBy: SortBy) => set({ sortBy }),
      setSortDirection: (sortDirection: SortDirection) =>
        set({ sortDirection }),
      setHeaderMessage: message => {
        set(
          produce(state => {
            state.headerMessages.push({
              ...message,
              timestamp: Date.now(),
            });
          })
        );
      },
      removeHeaderMessage: timestamp => {
        const filteredMessages = get().headerMessages.filter(
          message => message.timestamp !== timestamp
        );
        set({ headerMessages: filteredMessages });
      },
      setErrorMessage: message =>
        get().setHeaderMessage({ ...message, type: 'error' }),
      setSearchTerm: searchTerm => set({ searchTerm }),
      setActiveCollection: collection => {
        set({
          activeCollection: collection,
          activeView: collection ? 'collection_show' : 'collections',
        });
        useFlowStore.getState().resetSelectedFlows();
      },
      setRefetchCollections: refetch => set({ refetchCollections: refetch }),
      updateSelectedFilters: selectedFilters => set({ selectedFilters }),
    }),
    {
      name: 'flow-collection-store',
      partialize: (state: Store) => ({
        ...state,
        searchTerm: '',
        headerMessages: [],
      }),
    }
  )
);

export default useStore;
